@file:NoLiveLiterals

package com.example.borshi.web.home

import androidx.compose.runtime.NoLiveLiterals
import org.jetbrains.compose.web.attributes.AttrsScope


// ─────────────────────────────────────────────────────────────────────────────
// A11y — typed accessibility extensions for Compose for Web.
// Prevents silent typos in aria-* strings throughout the UI.
// ─────────────────────────────────────────────────────────────────────────────

enum class AriaRole(val value: String) {
    Button("button"),
    Link("link"),
    Navigation("navigation"),
    Main("main"),
    Banner("banner"),
    ContentInfo("contentinfo"),
    Alert("alert"),
    Status("status"),
    Dialog("dialog"),
    Menu("menu"),
    MenuItem("menuitem"),
    Tab("tab"),
    TabPanel("tabpanel"),
    TabList("tablist"),
    Region("region"),
    Presentation("presentation")
}

enum class AriaPoliteness(val value: String) {
    Off("off"),
    Polite("polite"),
    Assertive("assertive")
}

// ── Extension functions ───────────────────────────────────────────────────────

fun AttrsScope<*>.ariaLabel(label: String)        { attr("aria-label",       label) }
fun AttrsScope<*>.ariaDescribedBy(id: String)     { attr("aria-describedby", id) }
fun AttrsScope<*>.ariaLive(p: AriaPoliteness)     { attr("aria-live",        p.value) }
fun AttrsScope<*>.ariaExpanded(v: Boolean)        { attr("aria-expanded",    v.toString()) }
fun AttrsScope<*>.ariaHidden(v: Boolean)          { attr("aria-hidden",      v.toString()) }
fun AttrsScope<*>.ariaDisabled(v: Boolean)        { attr("aria-disabled",    v.toString()) }
fun AttrsScope<*>.ariaRole(role: AriaRole)        { attr("role",             role.value) }
fun AttrsScope<*>.tabIndex(index: Int)            { attr("tabindex",         index.toString()) }

/**
 * One-call shortcut: set aria-label + role + tabindex=0 on any interactive element.
 */
fun AttrsScope<*>.makeAccessible(
    label: String,
    role: AriaRole = AriaRole.Button,
    describedBy: String? = null
) {
    ariaLabel(label)
    ariaRole(role)
    tabIndex(0)
    if (describedBy != null) ariaDescribedBy(describedBy)
}

/**
 * Adds Enter / Space keyboard activation, mirroring click — needed for
 * non-<button> interactive elements (Div, Span used as buttons).
 */
fun AttrsScope<*>.onKeyActivate(action: () -> Unit) {
    onKeyDown { event ->
        if (event.key == "Enter" || event.key == " ") {
            event.preventDefault()
            action()
        }
    }
}
